import { computed, ref, watch } from '@vue/composition-api'
import formValidation from '@core/comp-functions/forms/form-validation'
import NotificationTypeDictionary from '@/dictionary/NotificationTypeDictionary'

export default function useSendMessageModal(props, { emit, root }) {
  const form = ref({
    subject: '',
    content: '',
    receiverType: null,
    bccSender: false,
  })
  const processing = ref(false)

  const isVisible = computed({
    get: () => props.value,
    set: val => emit('input', val),
  })

  const receiverOptions = computed(() => props.receivers.map(receiver => ({
    value: receiver.type,
    text: root.$t(receiver.label),
  })))

  const hasReceivers = computed(() => receiverOptions.value.length > 0)

  const receiverValidationRule = computed(() => (receiverOptions.value.length > 1 ? 'required' : ''))

  const setDefaultReceiver = () => {
    if (!hasReceivers.value) {
      form.value.receiverType = null
      return
    }
    if (props.defaultReceiverType && props.receivers.some(receiver => receiver.type === props.defaultReceiverType)) {
      form.value.receiverType = props.defaultReceiverType
      return
    }
    if (props.receivers.length === 1) {
      form.value.receiverType = props.receivers[0].type
      return
    }
    form.value.receiverType = null
  }

  const resetFormData = () => {
    form.value.subject = ''
    form.value.content = ''
    form.value.receiverType = null
    form.value.bccSender = false
    setDefaultReceiver()
  }

  const {
    refFormObserver,
    getValidationState,
    resetForm,
  } = formValidation(resetFormData)

  const contentLength = computed(() => form.value.content.length)

  const handleShow = () => {
    resetForm()
  }

  const handleHide = () => {
    form.value.subject = ''
    form.value.content = ''
    form.value.receiverType = null
    form.value.bccSender = false
    processing.value = false
  }

  watch(() => props.receivers, () => {
    setDefaultReceiver()
  })

  watch(() => props.defaultReceiverType, () => {
    setDefaultReceiver()
  })

  const submitCustomMessage = () => {
    if (!props.customerId || !hasReceivers.value) {
      return
    }
    const receiverType = form.value.receiverType || (props.receivers[0] ? props.receivers[0].type : null)
    if (!receiverType) {
      return
    }
    processing.value = true
    root.$store.dispatch('customer/sendCustomerMessage', {
      customerId: props.customerId,
      payload: {
        notificationType: NotificationTypeDictionary.CustomMessage,
        receiverType,
        subject: form.value.subject,
        content: form.value.content,
        bccSender: form.value.bccSender,
      },
    })
      .then(() => {
        window.toast.notify.success(root.$t('customers.send-message.success'))
        processing.value = false
        emit('input', false)
      })
      .catch(() => {
        window.toast.notify.error(root.$t('customers.send-message.error'))
        processing.value = false
      })
  }

  return {
    refFormObserver,
    form,
    isVisible,
    processing,
    receiverOptions,
    hasReceivers,
    receiverValidationRule,
    getValidationState,
    contentLength,
    submitCustomMessage,
    handleShow,
    handleHide,
  }
}
